import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Check, X, ThumbsUp, ThumbsDown, Loader, Image, Clock, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const VerifyDonation = () => {
  const { requestId, donorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [donation, setDonation] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch request and donation details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login', { state: { from: `/dashboard/requester/verify-donation/${requestId}/${donorId}` } });
          return;
        }

        // Fetch request details
        const requestResponse = await axios.get(`/api/requests/${requestId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Find the donor in the request
        const donorEntry = requestResponse.data.donors.find(
          d => d.donor === donorId || d.donor._id === donorId
        );

        if (!donorEntry) {
          setError('Donor not found in this request');
          setLoading(false);
          return;
        }

        setRequest(requestResponse.data);
        setDonation(donorEntry);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching donation details:', err);
        setError(err.response?.data?.message || 'Failed to load donation details');
        setLoading(false);
      }
    };

    fetchData();
  }, [requestId, donorId, navigate]);

  // Handle donation verification
  const handleVerification = async (status) => {
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `/api/requests/${requestId}/donation-status`,
        {
          donorId,
          donationStatus: status,
          feedback
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Donation ${status === 'confirmed' ? 'confirmed' : 'rejected'} successfully`);
      
      // Update local state
      setDonation({
        ...donation,
        status: status === 'confirmed' ? 'donated' : 'rejected',
        requesterFeedback: feedback
      });
      
      setUpdatingStatus(false);
    } catch (err) {
      console.error('Error updating donation status:', err);
      toast.error(err.response?.data?.message || 'Failed to update donation status');
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600">Loading donation details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-lg w-full">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Donation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dashboard/requester')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!request || !donation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-lg w-full">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Donation Not Found</h2>
            <p className="text-gray-600 mb-6">We couldn't find the donation you're looking for.</p>
            <button
              onClick={() => navigate('/dashboard/requester')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If donation is already verified or rejected
  if (donation.status === 'donated' || donation.status === 'rejected') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-lg w-full">
          <div className="flex flex-col items-center text-center">
            {donation.status === 'donated' ? (
              <Check className="w-16 h-16 text-green-500 mb-4" />
            ) : (
              <X className="w-16 h-16 text-red-500 mb-4" />
            )}
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {donation.status === 'donated' ? 'Donation Already Confirmed' : 'Donation Already Rejected'}
            </h2>
            <p className="text-gray-600 mb-6">
              {donation.status === 'donated' 
                ? 'You have already confirmed this donation.' 
                : 'You have already marked this donation as not received.'}
            </p>
            {donation.requesterFeedback && (
              <div className="bg-gray-50 p-4 rounded-lg mb-6 w-full">
                <p className="text-sm text-gray-500 mb-1">Your feedback:</p>
                <p className="text-gray-700 italic">"{donation.requesterFeedback}"</p>
              </div>
            )}
            <button
              onClick={() => navigate('/dashboard/requester')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-md overflow-hidden max-w-4xl w-full"
      >
        {/* Header */}
        <div className="bg-red-600 text-white p-6">
          <h1 className="text-2xl font-bold">Verify Blood Donation</h1>
          <p className="text-red-100">Please confirm if you received this blood donation</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Request Details */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Request Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Patient Name</p>
                <p className="font-medium text-gray-900">{request.patientName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Blood Type</p>
                <p className="font-medium text-gray-900">{request.bloodType || request.bloodGroup}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Units Needed</p>
                <p className="font-medium text-gray-900">{request.unitsNeeded}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Hospital</p>
                <p className="font-medium text-gray-900">{request.hospital?.name || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Urgency</p>
                <p className="font-medium text-gray-900">{request.urgency}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Required By</p>
                <p className="font-medium text-gray-900">{formatDate(request.requiredBy)}</p>
              </div>
            </div>
          </div>
          
          {/* Donor Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Donor Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{donation.donorName || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{donation.donorEmail || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{donation.donorPhone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Request Accepted On</p>
                <p className="font-medium text-gray-900">{donation.acceptedDate ? formatDate(donation.acceptedDate) : 'Not recorded'}</p>
              </div>
            </div>
          </div>
          
          {/* Donation Proof Photo */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-3">Donation Proof Photo</h2>
            {donation.donationProofPhoto ? (
              <div className="border rounded-lg overflow-hidden">
                <img 
                  src={donation.donationProofPhoto} 
                  alt="Donation Proof" 
                  className="w-full h-auto max-h-[400px] object-contain"
                />
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No photo provided</p>
              </div>
            )}
          </div>
          
          {/* Donor's Notes */}
          {donation.notes && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Donor's Notes</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 italic">"{donation.notes}"</p>
              </div>
            </div>
          )}
          
          {/* Feedback */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">Your Feedback (Optional)</h2>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows="3"
              placeholder="Add your feedback or thank you note for the donor..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            ></textarea>
          </div>
          
          {/* Verification Status */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-medium text-yellow-700">Awaiting Your Verification</h2>
            </div>
            <p className="text-sm text-yellow-600 mt-2">
              Please confirm whether you received this blood donation. This will notify the donor and update your request status.
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="p-6 border-t flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={() => navigate('/dashboard/requester')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Return to Dashboard
          </button>
          <button
            onClick={() => handleVerification('rejected')}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
            disabled={updatingStatus}
          >
            {updatingStatus ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <ThumbsDown className="w-4 h-4" />
            )}
            Not Received
          </button>
          <button
            onClick={() => handleVerification('confirmed')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            disabled={updatingStatus}
          >
            {updatingStatus ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <ThumbsUp className="w-4 h-4" />
            )}
            Confirm Donation
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyDonation;
